import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { COMMUNES, REGIONS } from '@/seo/communes';
import { Bell, Calendar, TrendingUp, Users, ArrowRight, MapPin, Sparkles, Radio, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Logo, LogoMark } from '@/components/Logo';

const HERO_IMG = 'https://images.unsplash.com/photo-1518766492976-82c449b7d616?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800';
const VOLCANO_IMG = 'https://images.unsplash.com/photo-1529391632125-77b37fc9e6a9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

const FEATURES = [
  { icon: Bell, title: 'Alertes temps réel', description: "Coupures d'eau, routes coupées, cyclones… informé instantanément par vos voisins.", tone: 'bg-red-500/10 text-red-500' },
  { icon: Calendar, title: 'Événements péi', description: 'Kabar, marchés, fêtes de quartier. Ne ratez plus rien dans votre commune.', tone: 'bg-amber-500/10 text-amber-500' },
  { icon: TrendingUp, title: 'Bons plans locaux', description: 'Promos au marché, ti punch offert chez Man Nick… les vrais bons plans du coin.', tone: 'bg-emerald-500/10 text-emerald-500' },
  { icon: Users, title: 'Vie de quartier', description: 'Chat par commune, entraide, commerces vérifiés : la Réunion qui se parle.', tone: 'bg-cyan-500/10 text-cyan-500' },
];

const PrimaryBtn = ({ children, onClick, testId, className = '' }) => (
  <button onClick={onClick} className={`inline-flex items-center justify-center gap-2 rounded-2xl volcano-gradient px-7 py-4 text-base font-semibold text-white shadow-xl shadow-primary/30 btn-bounce hover:brightness-110 ${className}`} data-testid={testId}>
    {children}
  </button>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) navigate('/feed', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts?limit=3`).then((r) => r.json()).then((d) => setStats(Array.isArray(d) ? d : [])).catch(() => setStats([]));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-5 sm:px-10 py-5 text-white">
        <Logo />
        <button onClick={() => navigate('/auth')} className="rounded-full border border-white/30 bg-white/10 backdrop-blur px-5 py-2 text-sm font-semibold hover:bg-white/20 transition" data-testid="landing-login-btn">Se connecter</button>
      </div>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end sm:items-center overflow-hidden">
        <img src={HERO_IMG} alt="Littoral de La Réunion" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(218_35%_9%)] via-[hsl(218_35%_9%/0.65)] to-[hsl(218_35%_9%/0.25)]" />
        <div className="relative z-10 container mx-auto max-w-6xl px-5 sm:px-10 pb-20 pt-32 sm:py-24 text-white">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] mb-6 animate-fade-in">
            <Radio className="h-3.5 w-3.5 text-secondary" /> 24 communes · temps réel
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] max-w-3xl animate-slide-up">
            Lo réseau social <span className="text-gradient">péi</span>,<br className="hidden sm:block" /> ton quartier en direct.
          </h1>
          <p className="text-base md:text-lg text-white/80 mt-6 max-w-xl leading-relaxed animate-slide-up" style={{ animationDelay: '80ms' }}>
            Alertes, événements, bons plans et vie de quartier : tout ce qui se passe à La Réunion, commune par commune, partagé par ceux qui y vivent.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-9 animate-slide-up" style={{ animationDelay: '160ms' }}>
            <PrimaryBtn onClick={() => navigate('/auth')} testId="cta-join-btn">Rejoindre gratuitement <ArrowRight className="h-5 w-5" /></PrimaryBtn>
            <button onClick={() => navigate('/calendar')} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/30 bg-white/10 backdrop-blur px-7 py-4 text-base font-semibold hover:bg-white/20 transition" data-testid="cta-discover-btn">
              Découvrir l'agenda
            </button>
          </div>
          <div className="flex items-center gap-6 mt-12 text-sm text-white/70">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Modération locale</span>
            <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold" /> 100 % gratuit</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-20 sm:py-28 px-5 sm:px-10 tropic-bg">
        <div className="container mx-auto max-w-6xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary mb-3">Pourquoi Bourbon</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold max-w-2xl leading-tight">Tout ce qui compte près de chez toi, sans le bruit du reste du monde.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12 stagger-children">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="post-card p-6" data-testid={`feature-card-${i}`}>
                <span className={`h-12 w-12 rounded-2xl inline-flex items-center justify-center mb-5 ${f.tone}`}><f.icon className="h-6 w-6" /></span>
                <h3 className="text-lg font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live preview */}
      <section className="px-5 sm:px-10 pb-20 sm:pb-28 tropic-bg">
        <div className="container mx-auto max-w-6xl grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div className="relative rounded-3xl overflow-hidden min-h-[320px] grain">
            <img src={VOLCANO_IMG} alt="Piton de la Fournaise" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 mb-2">En direct</p>
              <h3 className="text-2xl font-bold">Ce qui se passe maintenant</h3>
            </div>
          </div>
          <div className="space-y-3" data-testid="landing-live-posts">
            {(stats || [1, 2, 3]).slice(0, 3).map((p, i) => (
              typeof p === 'number' ? <div key={i} className="skeleton h-20" /> : (
                <Link key={p.post_id} to={`/post/${p.post_id}`} className="post-card p-4 flex items-center gap-4 block">
                  <span className="h-11 w-11 shrink-0 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center font-display font-bold">{p.commune?.charAt(0)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.commune}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              )
            ))}
            <Link to="/calendar" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline pt-2">Voir l'agenda des événements <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* Communes */}
      <section className="py-20 px-5 sm:px-10 bg-card border-y" data-testid="communes-section">
        <div className="container mx-auto max-w-6xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary mb-3">Toute l'île</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">Les <span className="text-gradient">24 communes</span> de La Réunion</h2>
          <p className="text-muted-foreground mb-12 max-w-xl">Alertes, événements, bons plans et infos : retrouve l'actualité de ta commune.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
            {REGIONS.map((region) => (
              <div key={region}>
                <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">{region}</h3>
                <ul className="space-y-2">
                  {COMMUNES.filter((c) => c.region === region).map((c) => (
                    <li key={c.id}>
                      <Link to={`/commune/${c.slug}`} className="group inline-flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors" data-testid={`landing-commune-${c.slug}`}>
                        {c.name}<ArrowRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 px-5 sm:px-10 overflow-hidden volcano-gradient grain text-white text-center">
        <div className="relative z-10 container mx-auto max-w-2xl">
          <LogoMark className="h-16 w-16 mx-auto mb-6 animate-float" />
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Rejoins ton quartier.</h2>
          <p className="text-white/85 mb-9 text-base md:text-lg">Gratuit, sans pub intrusive, pensé pour La Réunion.</p>
          <button onClick={() => navigate('/auth')} className="inline-flex items-center gap-2 rounded-2xl bg-white px-8 py-4 text-base font-bold text-primary shadow-2xl btn-bounce hover:bg-white/90" data-testid="cta-start-btn">
            Créer mon compte <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      <footer className="py-8 px-5 sm:px-10 text-center text-xs text-muted-foreground">
        <p>© {new Date().getFullYear()} Bourbon · Lo réseau social péi · La Réunion</p>
      </footer>
    </div>
  );
};

export default LandingPage;
